import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, setDoc, getDoc } from 'firebase/firestore';
import Notification from './Notification';
import Spinner from './Spinner';
import ProductForm from './ProductForm';
import ImageGallery from './ImageGallery';
import './Products.css';
import './QuantityStepper.css';
import { useTracking } from '../hooks/useTracking';
import { useEditMode } from '../contexts/EditModeContext';
import useGlobalTheme from '../hooks/useGlobalTheme';

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const Products = ({ cart, setCart }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [isVacationMode, setIsVacationMode] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const categoryDropdownRef = useRef(null);

  const { isDarkMode } = useGlobalTheme();

  const navigate = useNavigate();
  const { trackAddToCart, trackViewItem } = useTracking();
  const { isEditMode, isAdmin } = useEditMode();

  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

  // Función helper para mostrar notificaciones
  const showNotification = (message, type = 'success') => {
    setNotification({ message, show: true, type });
    setTimeout(() => {
      setNotification({ message: '', show: false });
    }, 3000);
  };

  const fetchProducts = useCallback(async () => {
    try {
      const productsCollection = await getDocs(collection(db, 'products'));
      const productsData = productsCollection.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setProducts(productsData);

      const fromCategory = productsData.map(p => p.category).filter(Boolean);
      const fromCategories = productsData.flatMap(p => Array.isArray(p.categories) ? p.categories : []);
      const uniqueCategories = [...new Set([...fromCategory, ...fromCategories])];
      setCategories(uniqueCategories);

      const vacationDoc = await getDoc(doc(db, 'settings', 'vacationMode'));
      if (vacationDoc.exists()) {
        setIsVacationMode(vacationDoc.data().isVacationMode);
        if (vacationDoc.data().isVacationMode) {
          showNotification('Estamos de vacaciones, volvemos pronto.', 'warning');
        }
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      showNotification('Error al cargar productos.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (!showProducts) return;
    const handlePointerDown = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setShowProducts(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [showProducts]);

  const addToCart = async (product, quantity) => {
    try {
      const productSnap = await getDoc(doc(db, 'products', product.id));
      if (!productSnap.exists()) {
        showNotification('Este producto ya no está disponible.', 'error');
        return;
      }

      const currentProduct = { ...productSnap.data(), id: productSnap.id };
      const existingProduct = cart.find(item => item.id === currentProduct.id);
      const existingQuantity = existingProduct ? existingProduct.quantity : 0;
      const noStockCheck = currentProduct.excludeFromStock === true;

      if (!noStockCheck && currentProduct.stock < quantity + existingQuantity) {
        showNotification('No hay suficiente stock disponible.', 'error');
        return;
      }

      const updatedCart = existingProduct
        ? cart.map(item => item.id === currentProduct.id ? { ...item, ...currentProduct, quantity: item.quantity + quantity } : item)
        : [...cart, { ...currentProduct, quantity }];

      setCart(updatedCart);
      showNotification(`Añadido ${quantity} ${currentProduct.model} al carrito.`, 'success');
      trackAddToCart({
        id: currentProduct.id,
        name: currentProduct.model,
        price: currentProduct.price,
        quantity
      });
    } catch (error) {
      console.error('Error refreshing product before adding to cart:', error);
      showNotification('No pudimos actualizar el precio del producto. Intentá nuevamente.', 'error');
    }
  };

  const handleAction = async (action, product) => {
    if (!isEditMode && (action === 'edit' || action === 'delete' || action === 'duplicate')) {
      navigate('/login');
      return;
    }
    if (action === 'edit') {
      setEditProduct(product);
      setIsModalOpen(true);
    } else if (action === 'delete') {
      handleDelete(product);
    } else if (action === 'view') {
      navigate(`/producto/${product.id}`);
      trackViewItem({
        id: product.id,
        name: product.model,
        price: product.price,
        quantity: 1
      });
    } else if (action === 'duplicate') {
      try {
        const newProductData = { ...product, model: `${product.model} (copy)` };
        delete newProductData.id;
        newProductData.images = [...product.images];
        await addDoc(collection(db, 'products'), newProductData);
        fetchProducts();
        showNotification('Producto duplicado con éxito.', 'success');
      } catch (error) {
        console.error("Error duplicating product: ", error);
        showNotification('Error al duplicar el producto.', 'error');
      }
    }
  };

  const handleDelete = async (product) => {
    const confirmDelete = window.confirm("¿Estás seguro de que deseas eliminar este producto?");
    if (confirmDelete) {
      try {
        await deleteDoc(doc(db, 'products', product.id));
        setProducts(products.filter(p => p.id !== product.id));
        showNotification('Producto eliminado con éxito.', 'success');
      } catch (error) {
        console.error("Error al eliminar el producto: ", error);
        showNotification('Error al eliminar el producto.', 'error');
      }
    }
  };

  const handleProductSubmit = async (productData) => {
    setLoading(true);
    try {
      if (editProduct) {
        await updateDoc(doc(db, 'products', editProduct.id), productData);
        setProducts(products.map(p => p.id === editProduct.id ? { ...productData, id: editProduct.id } : p));
        showNotification('Producto actualizado con éxito.', 'success');
      } else {
        const docRef = await addDoc(collection(db, 'products'), productData);
        setProducts([...products, { ...productData, id: docRef.id }]);
        showNotification('Producto agregado con éxito.', 'success');
      }
      setIsModalOpen(false);
      setEditProduct(null);
      fetchProducts();
    } catch (error) {
      console.error("Error al guardar el producto: ", error);
      showNotification('Error al guardar el producto.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVacationMode = async () => {
    if (isAdmin) {
      const newVacationMode = !isVacationMode;
      try {
        await setDoc(doc(db, 'settings', 'vacationMode'), { isVacationMode: newVacationMode });
        setIsVacationMode(newVacationMode);
        showNotification(
          newVacationMode ? 'Estamos de vacaciones, volvemos pronto.' : 'Modo vacaciones desactivado.',
          newVacationMode ? 'warning' : 'info'
        );
      } catch (error) {
        console.error("Error updating vacation mode: ", error);
        showNotification('Error al actualizar el modo vacaciones.', 'error');
      }
    }
  };

  const filteredProducts = products
    .filter(product =>
      (!product.hidden || isAdmin)
    )
    .sort((a, b) => a.model.localeCompare(b.model));

  if (loading) return <Spinner />;

  return (
    <div className={`products-container ${isDarkMode ? 'dark' : 'light'}`}>
      {notification.show && (
        <Notification 
          message={notification.message} 
          show={notification.show} 
          type={notification.type} 
        />
      )}
      
      <div className="products-header">
        <h1 className="products-title title-gradient-lm">Nuestra Colección</h1>
      </div>

      <div className="dropdown-container" ref={categoryDropdownRef}>
        <button type="button" className="category-button" onClick={() => setShowProducts(!showProducts)}>
         VER POR CATEGORÍAS
        </button>
        {showProducts && (
          <div className="dropdown-content-container">
            {categories
              .sort((a, b) => a.localeCompare(b))
              .map(category => (
                <button
                  key={category}
                  type="button"
                  onClick={() => {
                    setShowProducts(false);
                    navigate(`/categoria/${encodeURIComponent(category)}`);
                  }}
                  className="dropdown-content-button"
                >
                  {category}
                </button>
              ))}
          </div>
        )}
      </div>

      {isEditMode && (
        <div className="category-buttons-container">
          <button onClick={handleToggleVacationMode} className="category-button">
            {isVacationMode ? 'Desactivar Modo Vacaciones' : 'Activar Modo Vacaciones'}
          </button>
        </div>
      )}

      {isModalOpen && (
        <div
          className="modal-overlay-container"
          onClick={() => {
            setIsModalOpen(false);
            setEditProduct(null);
          }}
          role="presentation"
        >
          <div
            className="modal-content-container"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <ProductForm
              product={editProduct}
              onSubmit={handleProductSubmit}
              onCancel={() => {
                setIsModalOpen(false);
                setEditProduct(null);
              }}
              categories={categories}
              setCategories={setCategories}
            />
          </div>
        </div>
      )}

      <div className="ball-list-container">
        {filteredProducts
          .sort((a, b) => a.model.localeCompare(b.model))
          .map((product) => (
            <ProductItem
              key={product.id}
              product={product}
              addToCart={addToCart}
              isAuthenticated={isAuthenticated}
              isAdmin={isAdmin}
              handleAction={handleAction}
              isVacationMode={isVacationMode}
              showNotification={showNotification}
              isEditMode={isEditMode}
            />
          ))}
      </div>

      {isEditMode && (
        <button onClick={() => setIsModalOpen(true)} className="add-product-fab">
          +
        </button>
      )}
    </div>
  );
};

const MIN_PRODUCT_QTY = 1;

const ProductItem = ({ product, addToCart, isAuthenticated, isAdmin, handleAction, isVacationMode, showNotification, isEditMode }) => {
  const [quantity, setQuantity] = useState(1);
  const available = (product.excludeFromStock === true || product.stock > 0) && !isVacationMode;
  const maxQty = product.excludeFromStock === true ? 999 : product.stock;
  const upperBound = maxQty < MIN_PRODUCT_QTY ? MIN_PRODUCT_QTY : maxQty;

  const handleQuantityChange = (e) => {
    const raw = e.target.value;
    if (raw === '') return;
    const num = parseInt(raw, 10);
    if (Number.isNaN(num)) return;
    const clamped = Math.min(upperBound, Math.max(MIN_PRODUCT_QTY, num));
    setQuantity(clamped);
  };

  const handleQuantityBlur = (e) => {
    const raw = e.target.value.trim();
    if (raw === '') {
      setQuantity(MIN_PRODUCT_QTY);
      return;
    }
    const num = parseInt(raw, 10);
    if (Number.isNaN(num) || num < MIN_PRODUCT_QTY) {
      setQuantity(MIN_PRODUCT_QTY);
    } else if (num > upperBound) {
      setQuantity(upperBound);
    }
  };

  const handleQuantityStep = (delta) => {
    setQuantity((prev) => {
      const next = prev + delta;
      if (next >= MIN_PRODUCT_QTY && next <= upperBound) return next;
      return prev;
    });
  };

  return (
    <div className={`product-item-container ${!available && !isVacationMode ? 'out-of-stock' : ''} ${isVacationMode ? 'vacation-mode' : ''} ${product.hidden ? 'hidden' : ''}`}>
      <BallCard
        model={product.model}
        images={product.images || []}
        imagePositions={product.imagePositions}
        price={`$${formatNumber(product.price)}`}
        category={product.category}
        onClick={() => handleAction('view', product)}
      />
      <div className="product-actions-container">
        <div className="product-quantity-row">
          <label htmlFor={`quantity-${product.id}`} className="product-quantity-label">Cantidad</label>
          <div className="quantity-stepper">
            <button
              type="button"
              className="quantity-stepper-btn quantity-stepper-btn--minus"
              onClick={() => handleQuantityStep(-1)}
              disabled={!available || quantity <= MIN_PRODUCT_QTY}
              aria-label="Reducir cantidad"
            >
              −
            </button>
            <div className="quantity-stepper-field">
              <input
                type="number"
                id={`quantity-${product.id}`}
                value={quantity}
                min={MIN_PRODUCT_QTY}
                max={upperBound}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                className="quantity-input"
                aria-label="Cantidad"
                inputMode="numeric"
                pattern="[0-9]*"
                disabled={!available}
              />
            </div>
            <button
              type="button"
              className="quantity-stepper-btn quantity-stepper-btn--plus"
              onClick={() => handleQuantityStep(1)}
              disabled={!available || quantity >= upperBound}
              aria-label="Aumentar cantidad"
            >
              +
            </button>
          </div>
        </div>
        <button 
          className="product-action-button add" 
          onClick={() => addToCart(product, quantity)} 
          disabled={!available}
        >
          {product.stock > 0 || product.excludeFromStock ? 'Agregar al Carrito' : 'Sin Stock'}
        </button>
        {isEditMode && (
          <>
            <button className="product-action-button" onClick={() => handleAction('edit', product)}>
              Editar
            </button>
            <button className="product-action-button delete" onClick={() => handleAction('delete', product)}>
              Eliminar
            </button>
            <button className="product-action-button duplicate" onClick={() => handleAction('duplicate', product)}>
              Duplicar Producto
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const BallCard = ({ model, images, imagePositions, price, onClick }) => { 
  return (
    <div className="ball-card-container" onClick={onClick}> 
      <ImageGallery 
        images={images} 
        alt={model} 
        imagePositions={imagePositions}
        className="ball-card-gallery"
        cycleImagesOnHover
      />
      <h3 className="ball-card-title">{model}</h3>
      <p className="ball-card-price">{price}</p>
    </div>
  );
};

export default Products;